import { handleNativeAppExternalNavigationLink, isWebExperience } from './nativeAppUtils'
export interface FormatCentsOptions {
  /**
   * Display the number of cents (and the decimal point). When false, the decimal place will be omitted (e.g. $100 instead of $100.00) and the number of cents will be
   * rounded to the nearest whole dollar amount. Defaults to true.
   */
  cents?: boolean

  /**
   * Display cents only if cents are non-zero. If true, this option takes precedence over 'cents', i.e. cents will not be displayed if the value is
   * a whole-dollar amount, even if the 'cents' parameter is true. Similarly, if 'hideZeroCents' is true and 'cents' is false, cents will still be displayed
   * if the value is not a whole-dollar amount. Defaults to false.
   *
   * NOTE: Do not use this option to format a value that you expect to occasionally have non-whole-dollar amounts. Prefer consistently displaying the cents, even if zero, or
   * never displaying the cents, even if non-zero (in other words, use the 'cents' parameter instead). This flag is mostly useful in situations where you only expect
   * whole-dollar amounts, but where a non-whole-dollar amount MUST be displayed, for example in a credit card form or on an invoice.
   */
  hideZeroCents?: boolean

  /** When true, commas will be inserted as appropriate into the whole-dollar portion of the number, e.g. $1,000.56. Defaults to true. */
  commas?: boolean

  /** When true, the number will be prefixed with a dollar sign, e.g. $1,000.00. Defaults to true. */
  dollarSign?: boolean
}

const defaultFormatCentsOptions: FormatCentsOptions = {
  cents: true,
  commas: true,
  dollarSign: true,
};

export function format(first: string, middle: string, last: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

// sleep time expects it in milliseconds.
export function sleep (time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

export const tryParseJson = (obj: any) => {
  try {
    const o = JSON.parse(obj);
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) { /* empty */ }
  return obj;
};

export const openInNewTab = (href: string) => {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    rel: 'noopener noreferrer',
    href,
    role: 'button',
  }).click();
}

export const openPage = (href: string, isExternalNativeAppLink: boolean = false, event?: any) => {
  const thisWindow = window || globalThis?.window;
  if (typeof thisWindow !== 'undefined' && !isWebExperience && isExternalNativeAppLink && event) { // native app logic to open url in external browser in mobile devices
    handleNativeAppExternalNavigationLink(href, event);
  }
  else if (typeof thisWindow !== 'undefined') {
    thisWindow.location.href = href;
  }
};

export function getCookie(name) {
  function escape(s) {
    const escapeReturn = s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
    return escapeReturn;
  }
  let match;

  if (typeof window !== "undefined") {
    match = window?.document.cookie.match(RegExp("(?:^|;\\s*)" + escape(name) + "=([^;]*)"));
  }

  return match ? match[1] : null;
}

export function getCoxAutoZip() {
  const userLocationString = getCookie('x-coxauto-aka-data');
  const match = userLocationString?.match(/\b\d{5}\b/);
  return match ? match[0] : null;
}

export function isValidOfferAmount(offerValue: number, type: string, ownerListedPrice: number): boolean {
  return offerValue >= 100 && (type == 'joey-logano' ? offerValue >= ownerListedPrice : offerValue <= ownerListedPrice);
}

export const numberWithCommas = x => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const addDollarSign = x => {
  if (x && x.toString().charAt(0) != '$') {
    return '$' + x;
  }
  return x;
};

export function toTitleCase(str: string) {
  if (str) {
    /* Capitalize character after space */
    return str.replace(/\w\S*/g, function (text) {
      return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
    });
  } else {
    return str;
  }
}

export function dollarsToCents(dollars: number): number {
  throwIfNotFinite(dollars);

  return Math.round(dollars * 100);
}

export function formatCents(cents: number, { cents: displayCents = true, commas = true, dollarSign = true, hideZeroCents = false }: FormatCentsOptions = defaultFormatCentsOptions): string {
  throwIfNotFinite(cents);

  let fractionDigits: number;

  // when true, the 'hideZeroCents' option supersedes the 'cents' option
  if (hideZeroCents) {
    if (cents % 100 === 0) {
      fractionDigits = 0;
    } else {
      fractionDigits = 2;
    }
  } else {
    if (displayCents) {
      fractionDigits = 2;
    } else {
      fractionDigits = 0;
    }
  }

  if (dollarSign) {
    return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", currencyDisplay: "symbol", useGrouping: commas, minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
  } else {
    return formatDecimal(cents / 100, { commas, minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
  }
}
export function throwIfNotFinite(value: number) {
  if (!Number.isFinite(value)) {
    throw new Error(`Expected finite number but received '${value}' of type ${typeof value}`);
  }
}

export function formatDecimal(value: number, { minimumFractionDigits = 0, maximumFractionDigits = 0, commas = true } = {}) {
  throwIfNotFinite(value);

  return value.toLocaleString("en-US", { style: "decimal", useGrouping: commas, minimumFractionDigits, maximumFractionDigits });
}

export function validateZipCode(zipCode: string): boolean {
  const isValidZip = /^\d{5}(-\d{4})?$/.test(zipCode);
  return isValidZip;
}

export function getFallbackZipCode() {
  const akamaiZip = getCookie('ATC_USER_ZIP');
  const coxAutoZip = getCoxAutoZip();

  if (validateZipCode(akamaiZip)) {
    return akamaiZip;
  }
  if (validateZipCode(coxAutoZip)) {
    return coxAutoZip;
  }
  return null;
}

export function validateName(name: string) {
  return /^[a-zA-Z '-]{2,30}$/.test(name) && name.trim().length > 1;
}

export const validateNewOfferBusinessLogic = async (
  offerPrice: number | undefined,
  isSeller: boolean,
  ownerListedPrice: number,
  sellerLatestOfferPrice: number,
  buyerLatestOfferPrice: number
): Promise<{isValid: boolean, errorMessage: string}> => {
  let isValid = true;
  let errorMessage = "";

  // checking if offer is not a number or empty
  if (!offerPrice || !(/^[1-9]\d*$/.test(offerPrice?.toString().replace(/[$,]/g, '')))) {
    errorMessage = "Please enter a valid offer amount";
    isValid = false;
    return { isValid, errorMessage };
  }

  // checking if offer is higher than seller's asking price
  if (ownerListedPrice && (offerPrice > ownerListedPrice)) {
    errorMessage = "Offer is higher than the asking price";
    isValid = false;
    return { isValid, errorMessage };
  }

  // checking if offer is the same as the previous offer
  if ((isSeller && offerPrice === sellerLatestOfferPrice) || (!isSeller && offerPrice === buyerLatestOfferPrice)) {
    errorMessage = "Offer is the same as the previous offer";
    isValid = false;
    return { isValid, errorMessage };
  }

  // checking if price is lower than 100
  if (offerPrice < 100) {
    errorMessage = "Offer must be at least $100";
    isValid = false;
    return { isValid, errorMessage };
  }

  return { isValid, errorMessage };
};
